import type { ReplayApiTake } from "./replay-loader";

/**
 * Une prise, telle que l'écran la nomme.
 *
 * # Y compris les faux départs
 *
 * La liste porte **toutes** les prises que l'API connaît, et pas seulement
 * celles qui ont des notes. Un essai à zéro note — on lance, on s'arrête, on
 * recommence — n'a rien à rejouer, mais il occupe de la place et c'est le
 * premier qu'on voudra supprimer. Le taire l'aurait rendu ineffaçable.
 * `playable` dit lesquelles s'ouvrent.
 */
export interface ReplayTake {
  /** Ce que porte son lien partagé, `/takes/{id}`. */
  id: string;
  number: number;
  label: string;
  /**
   * Son nom propre, `null` sans : elle se partage alors sous celui de
   * l'enregistrement. C'est ce que l'interprète lui a donné depuis le studio.
   */
  title: string | null;
  notes: number;
  durationMs: number;
  /** Publiée : elle est visible de qui a le lien. */
  published: boolean;
  /** Elle porte des notes, donc un morceau de la séquence : `goToTake` l'ouvre. */
  playable: boolean;
}

/** Ce qu'un morceau de la séquence dit de la prise qu'il rejoue. */
export interface PlayedTake {
  number: number;
  notes: number;
}

/**
 * Les prises à l'écran : celles que l'API connaît, dans l'ordre.
 *
 * # Pourquoi la fiche et pas la séquence
 *
 * La séquence ne porte que ce qui se joue. Un faux départ — on lance, on
 * s'arrête au bout de trois secondes, on recommence — n'a pas de morceau, mais
 * il a une ligne en base, des octets dans le seau, et c'est le premier qu'on
 * veut supprimer. On part donc de la fiche, et le morceau ne sert qu'à dire ce
 * qu'il y a dedans.
 *
 * Une fiche sans prises rend une liste vide : le dépôt est d'une seule
 * traversée, ou d'avant le découpage. Il n'y a alors rien à choisir, et c'est
 * l'enregistrement entier qui se publie.
 *
 * # Le compte des notes vient du morceau quand il existe
 *
 * L'API compte les **paquets** que l'enregistreur a vus ; le morceau compte les
 * notes qu'on voit tomber. Les deux diffèrent — une pédale, un battement d'état
 * sont des paquets et pas des notes —, et c'est le second qu'on affiche à côté
 * d'une durée. L'API reste le repli : une prise que la séquence n'a pas bâtie
 * n'a que ce chiffre-là.
 *
 * Module pur : ni React, ni DOM.
 */
export function listTakes(
  known: readonly ReplayApiTake[] | undefined,
  played: readonly PlayedTake[],
): ReplayTake[] {
  if (known === undefined || known.length === 0) {
    return [];
  }

  return known.map((take) => {
    const song = played.find((one) => one.number === take.number);

    return {
      id: take.id,
      number: take.number,
      label: `Prise ${take.number}`,
      title: take.title ?? null,
      notes: song?.notes ?? take.notes ?? 0,
      durationMs: take.durationMs,
      published: take.published,
      playable: song !== undefined,
    };
  });
}

/**
 * Ce que le rouleau écrit au début de chaque prise jouée (les bornes de la
 * séquence, sur le rouleau et la minicarte).
 *
 * Le nom propre d'abord, partout : c'est celui que l'interprète a choisi, et
 * celui sous lequel la prise se partage. Sans lui, les deux regards diffèrent.
 * L'interprète lit « Prise 3 » : il sait combien il en a joué, et c'est ainsi
 * que le widget des prises les range. Un visiteur, jamais le rang — il dirait
 * combien d'essais il a fallu, ce que le lien d'une prise tait déjà
 * (2026-09-25) : il lit le nom de l'enregistrement, et deux prises qui le
 * partagent se distinguent par leur ordre à l'écran, « (2) », « (3) ».
 *
 * Une traversée unique n'a pas de borne à écrire : son étiquette est celle du
 * morceau, le nom de l'enregistrement.
 */
export function takeLabels(
  played: readonly { number: number }[],
  known: readonly ReplayApiTake[] | undefined,
  recordingTitle: string,
  mine: boolean,
): string[] {
  const titleOf = (number: number): string | null =>
    known?.find((take) => take.number === number)?.title ?? null;

  if (played.length === 1) {
    return [titleOf(played[0].number) ?? recordingTitle];
  }

  const seen = new Map<string, number>();

  return played.map(({ number }) => {
    const own = titleOf(number);

    if (own !== null) {
      return own;
    }

    if (mine) {
      return `Prise ${number}`;
    }

    const count = (seen.get(recordingTitle) ?? 0) + 1;

    seen.set(recordingTitle, count);

    return count === 1 ? recordingTitle : `${recordingTitle} (${count})`;
  });
}
